import React, { useContext } from "react";
import EqualColumnsGrid from "common/Grids/EqualColumnsGrid";
import TitleWithToggle from "features/bots/BotForm/common/TitleWithToggle";
import {
  StrategyTabs,
  ToggleField,
} from "@shared/features/bots/BotForm/common/types";
import { BotFormContext } from "@shared/features/bots/BotForm/common/BotForm.context";
import { BotType, LanguageNameSpaces, OrderTypes } from "@shared/types";
import BotStrategiesProvider from "@shared/features/bots/BotForm/Strategies/BotStrategiesProvider";
import useCustomStrategyForm from "features/configuration/GeneralConfiguration/ConfigurationItem/StrategyItem/useCustomStrategyForm";
import StrategyPrices from "features/bots/BotForm/Strategies/StrategyPrices";
import StrategyNumOrders from "features/bots/BotForm/Strategies/StrategyNumOrders";
import StrategyNumActiveOrders from "features/bots/BotForm/Strategies/StrategyNumActiveOrders";
import { BotBaseStrategiesContext } from "@shared/features/bots/BotForm/Strategies/BotBaseStrategiesProvider";
import useDCAStrategyForm from "features/bots/BotForm/Strategies/useDCAStrategyForm";
import MaxPriceDifference from "features/bots/BotForm/Strategies/MaxPriceDifference";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation.ts";

interface StrategyProps {
  orderType: OrderTypes.ENTRY | OrderTypes.TAKE_PROFIT;
  selectedTab?: StrategyTabs;
  tab: StrategyTabs;
}

const Strategy = ({ orderType, selectedTab, tab }: StrategyProps) => {
  return (
    <BotStrategiesProvider
      tab={tab}
      orderType={orderType}
      selectedTab={selectedTab}
    >
      <InnerStrategy tab={tab} />
    </BotStrategiesProvider>
  );
};

export default Strategy;

interface InnerStrategyProps {
  tab: StrategyTabs;
}
const InnerStrategy = ({ tab }: InnerStrategyProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const { botType } = useContext(BotFormContext) || {};
  const { singleColumnLayout } = useContext(BotBaseStrategiesContext)!;
  const isSignalsBot = botType === BotType.SIGNAL;
  const isHedgingBot = botType === BotType.HEDGING;
  const { firstOrderPercentSelector, amountScaleSelector, baselineSelector } =
    useDCAStrategyForm();

  const { targetsComponent } = useCustomStrategyForm({
    style: isSignalsBot ? { marginTop: 0 } : undefined,
  });

  const getFirstOrder = () => {
    if (isHedgingBot) return;
    if (!firstOrderPercentSelector) return;
    return (
      <div style={{ marginBottom: 16 }}>
        <TitleWithToggle
          title={t("labels.first_entry_amount")}
          content={
            <EqualColumnsGrid
              numColumns={singleColumnLayout ? 1 : 2}
              style={{ marginTop: 8, paddingLeft: 4 }}
            >
              <div>{firstOrderPercentSelector}</div>
              <div />
            </EqualColumnsGrid>
          }
          fieldType={ToggleField.FIRST_ENTRY}
        />
        <div />
      </div>
    );
  };

  return (
    <>
      {getFirstOrder()}
      <EqualColumnsGrid numColumns={singleColumnLayout ? 1 : 2} rowGap={4}>
        {!isHedgingBot &&
          baselineSelector && [baselineSelector, <div key="placeholder" />]}
        {!isSignalsBot && <StrategyPrices />}
        {!isHedgingBot && amountScaleSelector}
        {!isSignalsBot && !isHedgingBot && <StrategyNumOrders />}
        {!isSignalsBot && !isHedgingBot && <StrategyNumActiveOrders />}
      </EqualColumnsGrid>
      {!isSignalsBot && <MaxPriceDifference />}
      {tab === StrategyTabs.CUSTOM && targetsComponent}
    </>
  );
};
